import { fetcher } from "../api/fetcher";
import { activeRoomState } from "../states/active_room";
import { authState } from "../states/auth";
import { roomState, serverDiffTime } from "../states/room";
import {
  Participant,
  RemoteParticipant,
  RemoteTrack,
  RemoteTrackPublication,
  RoomEvent,
  Track,
  VideoPresets,
} from "livekit-client";
import { trackToSubMap, usersVideoTracks } from "../states/usersVideoStreams";
import { ref } from "vue";
import { setSpeakers } from "../states/currentSpeakers";
import { goFinish } from "../states/finishState";
import { watch5MinNotification } from "./watch5MinNotification";
import { firstConnectSyncInfo, subscribe_sync_info } from "./syncInfo";
import { domains } from "../states/domains";
import {
  newScreenTrackByLivekit,
  removeScreenTrackByLivekit,
  subscribeScreenShareEvents,
} from "../states/screenShareState";
import { subscribeRecordEvents } from "../states/recordState";
import {
  subscribeConnectUser,
  subscribeDisconnectUser,
  subscribeKickUser,
  subscribeUpdateUser,
  usersState,
} from "../states/usersState";
import { getInitialMessages, subscribeChatMessages } from "../states/chatState";
import { subscribeHandUp } from "../states/handUpState";
import { showWaitRoomNotification } from "../states/waitRoomState";
import { router } from "../router";
import {
  desiredMediaState,
  subscribeAllMicroOff,
  subscribeMuteUser,
} from "../states/media/state";
import { apiConnected } from "../states/media/api";
import {
  createLivekitRoomInstance,
  setLivekitPublishReady,
} from "../states/media/livekit";
import { audioContext, resumeAudioContext } from "../states/audioCtx";
import { mediaActions } from "../states/media/actions";

export const first_connected = ref(false);

export async function connectToActiveRoom() {
  subscribeConnectUser();
  subscribeUpdateUser();
  subscribeKickUser();
  subscribeDisconnectUser();
  subscribeScreenShareEvents();
  subscribeAllMicroOff();
  subscribeMuteUser();
  subscribe_sync_info();
  subscribeRecordEvents();
  subscribeChatMessages();
  subscribeHandUp();
  const deviceStates = {
    micro: desiredMediaState.microEnabled,
    camera: desiredMediaState.cameraEnabled,
  };
  const user = {
    id: authState.id,
    displayedName: authState.displayedName,
    avatar: authState.avatar,
    micro: deviceStates.micro,
    camera: deviceStates.camera,
  };

  const res = (await fetcher
    .post({
      path: "/active-room/connect",
      payload: {
        user: user,
        room_id: roomState.id,
      },
    })
    .catch((err) => {
      if (err?.data?.message === "you kicked") {
        goFinish("kick");
      } else if (err?.data?.message === "you not participant") {
        router.push({
          name: "error",
          params: {
            errtype: "not-participant",
          },
        });
      } else {
        goFinish("network");
      }
      throw "not allowed";
    })) as {
    token: string;
    serverTime: number;
  };

  activeRoomState.selfApiConnected = true;
  apiConnected(deviceStates);
  showWaitRoomNotification();

  getInitialMessages();

  serverDiffTime.value = Date.now() - res.serverTime;
  watch5MinNotification();

  const room = createLivekitRoomInstance({
    adaptiveStream: true,
    dynacast: true,
    videoCaptureDefaults: {
      resolution: VideoPresets.h720.resolution,
    },
    webAudioMix: audioContext
      ? {
          audioContext: audioContext,
        }
      : undefined,
  });

  room
    .on(RoomEvent.Disconnected, handleDisconnected)
    .on(RoomEvent.TrackSubscribed, handleTrackSubscribed)
    .on(RoomEvent.TrackUnsubscribed, handleTrackUnsubscribed)
    .on(RoomEvent.ActiveSpeakersChanged, handleActiveSpeakerChange)
    .on(RoomEvent.AudioPlaybackStatusChanged, handleAudioPlaybackStatusChanged);

  function handleAudioPlaybackStatusChanged() {
    resumeAudioContext();
    room.startAudio().catch((err) => console.log("start audio err", err));
  }

  function handleTrackSubscribed(
    track: RemoteTrack,
    s: RemoteTrackPublication,
    user: RemoteParticipant,
  ) {
    trackToSubMap.set(track, s);
    if (track.kind === Track.Kind.Audio) {
      resumeAudioContext();
      track.attach();
    } else if (track.kind === Track.Kind.Video) {
      if (track.source === "screen_share") {
        //@ts-ignore
        s.track.adaptiveStreamSettings = {
          pixelDensity: window.devicePixelRatio + 2,
        };
        newScreenTrackByLivekit(track, user.identity);
        track.once("ended", () => {
          removeScreenTrackByLivekit(user.identity);
        });
      } else {
        usersVideoTracks[user.identity] = track;
      }
    }
  }

  function handleTrackUnsubscribed(
    track: RemoteTrack,
    _pub: RemoteTrackPublication,
    user: RemoteParticipant,
  ) {
    trackToSubMap.delete(track);
    if (track.kind === Track.Kind.Audio) {
      track.detach();
    } else if (track.kind === Track.Kind.Video) {
      track.detach();
      if (track.source === "screen_share") {
        removeScreenTrackByLivekit(user.identity);
      } else {
        usersVideoTracks[user.identity] = null;
      }
    }
  }

  function handleActiveSpeakerChange(speakers: Participant[]) {
    setSpeakers(speakers.map((s) => s.identity));
  }

  function handleDisconnected() {
    if (first_connected.value) {
      goFinish("network");
    }
  }

  for (let i = 0; i < 3; i++) {
    try {
      await room.connect("wss://" + domains.livekit_host, res.token, {
        websocketTimeout: 5000,
        peerConnectionTimeout: 5000,
        maxRetries: 3,
      });
      break;
    } catch {
      if (i == 2) {
        let tlsturn: {
          credential: string;
          username: string;
          urls: string[];
        } | null = null;
        if (room.engine.latestJoinResponse?.iceServers) {
          for (const endpoint of room.engine.latestJoinResponse?.iceServers) {
            if (endpoint.credential) {
              const url = endpoint.urls.find((u) => {
                return u.startsWith("turns:") && u.includes("443");
              });
              if (url) {
                tlsturn = {
                  urls: [url],
                  username: endpoint.username,
                  credential: endpoint.credential,
                };
                break;
              } else continue;
            } else continue;
          }
        }
        if (tlsturn) {
          try {
            await room.connect("wss://" + domains.livekit_host, res.token, {
              rtcConfig: {
                iceServers: [tlsturn],
                iceTransportPolicy: "relay",
              },
              maxRetries: 3,
            });
          } catch {
            goFinish("first-connect");
            throw "err on first connect";
          }
        } else {
          goFinish("first-connect");
          throw "err on first connect";
        }
      }
    }
  }

  first_connected.value = true;

  resumeAudioContext();

  usersState.users.unshift({
    id: authState.id,
    avatar: authState.avatar,
    micro: deviceStates.micro,
    camera: deviceStates.camera,
    displayedName: authState.displayedName,
    joined: Date.now(),
  });

  await firstConnectSyncInfo();
  if (activeRoomState.allMute && activeRoomState.allMute !== authState.id) {
    mediaActions.microOff();
  }
  setLivekitPublishReady(true);
}
